"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { fadeInAnimationVariants } from "@/lib/animation";
import { Button } from "@/components/ui/Button";

const kartelaPdfAbsoluteUrl = `${(process.env.NEXT_PUBLIC_SITE_URL || "https://filizkozmetik.net").replace(/\/$/, "")}/catalog/kartela.pdf`;

export default function KartelaPage() {
  const [isLoading, setIsLoading] = useState(true);

  const handlePdfLoad = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    const timeout = setTimeout(() => setIsLoading(false), 5000);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <main className="min-h-screen">
      <section className="bg-gradient-to-b from-primary/5 to-primary/0 pt-10 pb-16">
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center md:flex-row">
            <div className="md:w-1/2 md:pr-10">
              <motion.h1
                className="mb-4 text-4xl font-bold text-primary-dark md:text-5xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                E-Kartela
              </motion.h1>
              <motion.p
                className="mb-6 text-lg text-gray-700"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Filiz Kozmetik&apos;un geniş renk paletini keşfedin. Modern ve klasik dekorasyon stillerine uygun renk
                kartelalarımızı inceleyebilir, projenize en uygun renkleri seçebilirsiniz.
              </motion.p>
              <motion.div
                className="mb-8 md:mb-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Button className="flex items-center rounded-lg bg-primary px-5 py-2 font-medium text-white hover:bg-primary/90" asChild>
                  <a href="/catalog/kartela.pdf" download="filiz-kozmetik-kartela.pdf">
                    <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Kartelayı İndir
                  </a>
                </Button>
              </motion.div>
            </div>
            <div className="md:w-1/2">
              <motion.div
                className="relative overflow-hidden rounded-lg shadow-xl"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Image
                  src="/images/salon-showcase-2.webp"
                  alt="Filiz Kozmetik Renk Kartelası"
                  width={600}
                  height={400}
                  className="h-auto w-full rounded-lg"
                  priority
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-6">
          <div className="flex flex-col gap-8 lg:flex-row">
            <motion.div
              className="relative rounded-xl border bg-white p-2 shadow-lg md:p-4 lg:w-2/3"
              variants={fadeInAnimationVariants}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              custom={0}
            >
              <h2 className="mb-4 text-2xl font-bold text-primary-dark">Renk Kartelamız</h2>
              <p className="mb-6 text-gray-700">
                Aşağıdaki PDF görüntüleyicide renk kartelamızı inceleyebilirsiniz. Daha detaylı görüntülemek için tam ekran
                modunu kullanabilir veya PDF&apos;i indirebilirsiniz.
              </p>

              {isLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                  <div className="flex flex-col items-center">
                    <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-primary" />
                    <p className="mt-4 font-medium text-primary-dark">Kartela yükleniyor...</p>
                  </div>
                </div>
              )}

              <div className="h-[600px] overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(kartelaPdfAbsoluteUrl)}&embedded=true`}
                  className="h-[600px] w-full"
                  onLoad={handlePdfLoad}
                  title="Filiz Kozmetik Renk Kartelası"
                  frameBorder={0}
                />
              </div>

              <div className="mt-6 flex justify-center">
                <Button className="flex items-center rounded-lg bg-primary px-5 py-2 font-medium text-white hover:bg-primary/90" asChild>
                  <a href="/catalog/kartela.pdf" download="filiz-kozmetik-kartela.pdf">
                    <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Kartelayı İndir
                  </a>
                </Button>
              </div>
            </motion.div>

            <motion.div
              className="lg:w-1/3"
              variants={fadeInAnimationVariants}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              custom={1}
            >
              <div className="mb-6 rounded-xl bg-white p-6 shadow-lg">
                <h2 className="mb-4 text-2xl font-bold text-primary-dark">Renk Özelliklerimiz</h2>
                <Separator className="my-4" />

                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="mr-4 rounded-full bg-primary/10 p-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary-dark">Zengin Renk Seçenekleri</h3>
                      <p className="mt-1 text-gray-600">200+ farklı renk tonu ile her tarza ve zevke uygun seçenekler sunuyoruz.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="mr-4 rounded-full bg-primary/10 p-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary-dark">Kalıcı ve Dayanıklı</h3>
                      <p className="mt-1 text-gray-600">Solmaya karşı dayanıklı, uzun ömürlü ve yıkanabilir renk teknolojisi.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="mr-4 rounded-full bg-primary/10 p-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary-dark">Hızlı Kuruma</h3>
                      <p className="mt-1 text-gray-600">Hızlı kuruyan formülüyle projelerinizi kısa sürede tamamlayın.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="mr-4 rounded-full bg-primary/10 p-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary-dark">Özel Üretim İmkanı</h3>
                      <p className="mt-1 text-gray-600">Özel projeleriniz için kişiye özel renk üretimi yapabiliyoruz.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-primary/5 p-6">
                <h3 className="mb-3 text-xl font-bold text-primary-dark">Renk Danışmanlığı</h3>
                <p className="mb-4 text-gray-700">
                  Uzman ekibimiz, mekanınıza en uygun renk seçiminde size yardımcı olmaktan mutluluk duyacaktır. Ücretsiz renk
                  danışmanlığı hizmetimizden faydalanmak için bize ulaşın.
                </p>
                <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white" asChild>
                  <Link href="/iletisim" className="inline-flex items-center justify-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Danışmanlık İste
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
